import { LitElement, html, css, svg, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import * as store from "../state/store.js";
import { SignalWatcher } from "../state/signal-watcher.js";
import { MM_PER_UNIT } from "../state/types.js";
import {
  closeDxf,
  loadDxfFromFile,
  openFilePicker,
} from "../ops/io.js";
import {
  openModal,
  toggleGrid,
  toggleGridRuler,
  toggleSidebar,
  toggleTheme,
  setViewMode,
  fitCurrentView,
} from "../ops/ui.js";
import { tooltip } from "../utils/tooltip.js";

// Inline SVG icon paths (Lucide-derived, 24x24 viewbox, stroked with
// currentColor so hover / active / disabled states just work).
const ICONS = {
  open: svg`<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>`,
  close: svg`<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>`,
  export: svg`<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>`,
  fit: svg`<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>`,
  fullscreen: svg`<polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>`,
  grid: svg`<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 12h18"/><path d="M12 3v18"/>`,
  ruler: svg`<path d="M21.3 8.7 15.3 2.7a1 1 0 0 0-1.4 0L2.7 13.9a1 1 0 0 0 0 1.4l6 6a1 1 0 0 0 1.4 0L21.3 10.1a1 1 0 0 0 0-1.4z"/><path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/><path d="m13.5 4.5 2 2"/><path d="m4.5 13.5 2 2"/>`,
  layers: svg`<path d="m12 2 8 4-8 4-8-4 8-4z"/><path d="m4 12 8 4 8-4"/><path d="m4 17 8 4 8-4"/>`,
  settings: svg`<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>`,
  sun: svg`<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>`,
  moon: svg`<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/>`,
  more: svg`<circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>`,
};

@customElement("editor-topbar")
export class EditorTopbar extends SignalWatcher(LitElement) {
  // Overflow menu state — controls the mobile kebab dropdown. Not stored in
  // the global store because it's ephemeral chrome state that dies with the
  // component; keeping it local keeps the store lean.
  @state()
  private overflowOpen = false;

  override connectedCallback(): void {
    super.connectedCallback();
    // Fullscreen state lives on document, not in the signal store — subscribe
    // so the button's active/label toggles when the user hits Esc or the OS
    // exits fullscreen for us.
    document.addEventListener("fullscreenchange", this.#onFullscreenChange);
    // Close the overflow menu on any outside click.
    document.addEventListener("click", this.#onDocClick, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("fullscreenchange", this.#onFullscreenChange);
    document.removeEventListener("click", this.#onDocClick, true);
  }

  #onFullscreenChange = () => this.requestUpdate();

  #onDocClick = (e: MouseEvent) => {
    if (!this.overflowOpen) return;
    const path = e.composedPath();
    for (const node of path) {
      if (!(node instanceof Element)) continue;
      if (node.classList?.contains("overflow-menu")) return;
      if (node.classList?.contains("overflow-btn")) return;
    }
    this.overflowOpen = false;
  };

  #toggleOverflow = () => {
    this.overflowOpen = !this.overflowOpen;
  };

  #closeOverflow = () => {
    this.overflowOpen = false;
  };

  #toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      // The editor root is the closest host we own; requesting fullscreen on
      // it keeps the layout self-contained rather than swallowing the whole
      // Rack/Stimulus page.
      const target = (this.getRootNode() as ShadowRoot).host ?? this;
      void (target as HTMLElement).requestFullscreen();
    }
  };

  static override styles = css`
    :host {
      display: flex;
      align-items: center;
      height: var(--topbar-height, 42px);
      padding: 0 16px;
      padding-left: max(16px, var(--safe-left, 0px));
      padding-right: max(16px, var(--safe-right, 0px));
      background: var(--bg-tertiary);
      border-bottom: 1px solid var(--border-color);
      color: var(--text-primary);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      flex-shrink: 0;
      position: relative;
    }

    .left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .title {
      font-size: 13px;
      font-weight: 600;
      color: var(--text-white);
      letter-spacing: 0.02em;
    }

    /* Compact mobile brand: the drafting triangle glyph in a subtle
       accent-tinted pill so it reads as a mark, not stray emoji. Only
       rendered on mobile; desktop keeps the "DXF Layer Editor" wordmark. */
    .brand-mark {
      display: none;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border-radius: 9px;
      background: color-mix(in oklab, var(--accent) 14%, transparent);
      border: 1px solid color-mix(in oklab, var(--accent) 22%, transparent);
      font-size: 18px;
      line-height: 1;
      /* Keep the glyph vertically centered — some emoji fonts have odd
         baselines. */
      padding-bottom: 1px;
      user-select: none;
      -webkit-user-select: none;
    }

    .filename {
      font-size: 12px;
      font-style: italic;
      color: var(--text-secondary);
      margin-left: 8px;
      max-width: 480px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .filename .dims {
      font-style: normal;
      opacity: 0.75;
      margin-left: 4px;
      font-variant-numeric: tabular-nums;
    }

    .center {
      flex: 1;
    }

    .actions {
      display: flex;
      align-items: center;
      gap: 2px;
    }

    button {
      background: transparent;
      border: none;
      color: var(--text-secondary);
      cursor: pointer;
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      padding: 0;
      transition: background 0.15s, color 0.15s;
    }

    button:hover:not(:disabled) {
      background: var(--bg-hover);
      color: var(--text-primary);
    }

    button:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    button.active {
      color: var(--accent);
      background: var(--bg-hover);
    }

    /* Featured (primary-CTA) variant: solid accent background with a soft
       ambient glow so the button reads as the room's main action among a
       row of outline icons. Slow gentle pulse announces it without being
       distracting; the pulse is suppressed on hover so the pointer response
       feels crisp. Disabled state fades but keeps the accent tint so the
       button's identity survives "no file loaded". */
    button.featured {
      color: #fff;
      background: var(--accent);
      box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 55%, transparent);
      animation: featured-pulse 2.4s ease-in-out infinite;
    }
    button.featured:hover:not(:disabled) {
      background: var(--accent-hover);
      color: #fff;
      animation: none;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-hover) 35%, transparent);
    }
    button.featured:disabled {
      background: color-mix(in srgb, var(--accent) 30%, transparent);
      color: #fff;
      opacity: 0.55;
      animation: none;
      box-shadow: none;
    }
    @keyframes featured-pulse {
      0%, 100% {
        box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 55%, transparent);
      }
      50% {
        box-shadow: 0 0 0 6px color-mix(in srgb, var(--accent) 0%, transparent);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      button.featured { animation: none; }
    }

    .separator {
      width: 1px;
      height: 20px;
      background: var(--border-color);
      margin: 0 6px;
    }

    /* 2D/3D segmented toggle. Both labels are always shown; the active
       viewMode gets accent color + bg so the current mode is unambiguous.
       The 3D half fades to disabled until the background GLB build finishes
       (matches the SolidWorks pattern of showing both modes with the
       inactive one dimmed). */
    .mode-toggle {
      display: inline-flex;
      align-items: center;
      background: var(--bg-secondary, var(--bg-hover));
      border: 1px solid var(--border-color);
      border-radius: 4px;
      padding: 2px;
      margin: 0 4px;
    }
    .mode-toggle button {
      width: auto;
      height: 24px;
      padding: 0 10px;
      border-radius: 2px;
      font-family: inherit;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      color: var(--text-secondary);
    }
    .mode-toggle button.active {
      color: #fff;
      background: var(--accent);
    }
    .mode-toggle button.active:hover:not(:disabled) {
      background: var(--accent-hover, var(--accent));
      color: #fff;
    }
    .mode-toggle button:disabled {
      opacity: 0.4;
    }

    svg {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    /* GLB-build progress indicator: a compact 20px ring shown between the
       separator and the 3D-cube button while store.glbActive is true. The
       animated stroke-dashoffset drives fill from empty to full as the SSE
       stream reports percentages; when the ring completes it disappears
       and the cube button lights up. */
    .glb-progress {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin: 0 4px;
      color: var(--accent);
    }
    .glb-progress svg {
      width: 20px;
      height: 20px;
      stroke-width: 2.5;
      /* Rotate so the arc starts at 12 o'clock and grows clockwise. */
      transform: rotate(-90deg);
    }
    .glb-progress .track {
      stroke: var(--border-color);
    }
    .glb-progress .fill {
      stroke: var(--accent);
      transition: stroke-dashoffset 0.2s linear;
    }

    /* Mobile-only chrome: the overflow (kebab) menu. Fixed 260px width so
       labels never wrap (min-width was insufficient — the flex context
       inside :host + the absolutely-positioned menu could still collapse
       to intrinsic content width in some browsers). Anchored to the right
       edge with a small caret pointing at the kebab, animated open with a
       subtle scale + fade so it feels tactile. */
    .overflow-menu {
      position: absolute;
      top: calc(100% + 8px);
      right: max(8px, var(--safe-right, 0px));
      width: 260px;
      max-width: calc(100vw - 16px);
      background: var(--sheet-bg, var(--bg-tertiary));
      border: 1px solid var(--border-color);
      border-radius: 12px;
      padding: 6px;
      box-shadow:
        0 1px 0 0 rgba(255, 255, 255, 0.04) inset,
        0 12px 40px var(--shadow, rgba(0, 0, 0, 0.55)),
        0 2px 6px rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(14px) saturate(1.1);
      display: flex;
      flex-direction: column;
      gap: 1px;
      z-index: 900;
      transform-origin: top right;
      animation: overflow-in 160ms cubic-bezier(0.22, 1, 0.36, 1);
    }
    /* Little caret pointing at the kebab button so the menu reads as
       "attached" to the trigger, not a floating slab. */
    .overflow-menu::before {
      content: "";
      position: absolute;
      top: -6px;
      right: 16px;
      width: 12px;
      height: 12px;
      background: var(--sheet-bg, var(--bg-tertiary));
      border-left: 1px solid var(--border-color);
      border-top: 1px solid var(--border-color);
      transform: rotate(45deg);
      border-top-left-radius: 3px;
    }
    @keyframes overflow-in {
      from {
        opacity: 0;
        transform: scale(0.94) translateY(-6px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }
    .overflow-menu .row {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 0 14px;
      background: transparent;
      border: none;
      border-radius: 8px;
      color: var(--text-primary);
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.005em;
      cursor: pointer;
      text-align: left;
      height: 44px;
      width: 100%;
      box-sizing: border-box;
      transition: background 0.12s ease, color 0.12s ease;
      /* Kill UA button chrome so the row background renders cleanly. */
      appearance: none;
      -webkit-appearance: none;
    }
    .overflow-menu .row:hover:not(:disabled),
    .overflow-menu .row:focus-visible:not(:disabled) {
      background: color-mix(in oklab, var(--accent) 16%, transparent);
      color: var(--text-white);
      outline: none;
    }
    .overflow-menu .row:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
    .overflow-menu .row.active {
      color: var(--accent);
    }
    .overflow-menu .row.active:hover:not(:disabled) {
      color: var(--text-white);
    }
    .overflow-menu .row svg {
      width: 18px;
      height: 18px;
      flex: 0 0 18px;
      stroke: currentColor;
      opacity: 0.9;
    }
    .overflow-menu .row .label {
      flex: 1 1 auto;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
    /* "On" indicator for toggleable rows (Grid, Ruler). A crisp pill instead
       of a dot — reads as a proper stateful control. */
    .overflow-menu .row .check {
      flex: 0 0 auto;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--accent);
      background: color-mix(in oklab, var(--accent) 18%, transparent);
      padding: 3px 7px;
      border-radius: 999px;
      opacity: 0;
      transition: opacity 0.12s ease;
    }
    .overflow-menu .row.active .check {
      opacity: 1;
    }
    .overflow-menu .divider {
      height: 1px;
      background: var(--border-color);
      margin: 5px 10px;
      opacity: 0.7;
    }

    /* Mobile responsive rules. Kicks in at the same 720px breakpoint the
       store's isMobile() signal uses, so JS-side layout swaps (bottom-
       sheet layer panel, kebab overflow) and CSS-side chrome tightening
       stay in lockstep. */
    @media (max-width: 720px) {
      :host {
        height: var(--topbar-height-mobile, 52px);
        padding: 0 10px;
        padding-left: max(10px, var(--safe-left, 0px));
        padding-right: max(10px, var(--safe-right, 0px));
      }
      /* Trade the "DXF Layer Editor" wordmark for the drafting glyph so
         the top-left still has a visual anchor. Filename subtitle stays
         hidden — the status bar has it. */
      .title { display: none; }
      .brand-mark { display: inline-flex; }
      .filename { display: none; }
      .left { gap: 6px; }
      .actions { gap: 2px; }
      button {
        width: var(--tap-target-mobile, 44px);
        height: var(--tap-target-mobile, 44px);
      }
      button svg { width: 22px; height: 22px; }
      .separator { display: none; }
      /* Hide the secondary chrome buttons — they move into the overflow menu.
         Kept in the DOM tree only when NOT on mobile so we don't ship dead
         nodes; see the render() branch. */
      .mobile-hidden { display: none !important; }
      .mode-toggle { margin: 0 2px; padding: 3px; }
      .mode-toggle button {
        height: 32px;
        min-width: 36px;
        padding: 0 10px;
        font-size: 12px;
      }
    }

    /* Even narrower phones — 360px iPhone SE, 375–393px modern iPhones —
       need a tighter cadence so the 6 primary buttons + kebab still fit
       without wrapping. The brand-mark stays visible (users expect a top-
       left anchor); it shrinks to 28px and everything else tightens up. */
    @media (max-width: 400px) {
      :host {
        padding: 0 6px;
        padding-left: max(6px, var(--safe-left, 0px));
        padding-right: max(6px, var(--safe-right, 0px));
      }
      button {
        width: 38px;
        height: 38px;
      }
      button svg { width: 20px; height: 20px; }
      .brand-mark {
        width: 28px;
        height: 28px;
        border-radius: 7px;
        font-size: 15px;
      }
      .left { gap: 4px; }
      .actions { gap: 0; }
      .mode-toggle { margin: 0; padding: 2px; }
      .mode-toggle button { padding: 0 7px; min-width: 30px; font-size: 11px; }
    }
  `;

  override render() {
    const loaded = store.isLoaded();
    const gridOn = store.showGrid();
    const rulerOn = store.showGridRuler();
    const sidebarVisible = store.sidebarVisible();
    const isDark = store.theme() === "dark";
    const filename = store.filename();
    const isFullscreen = !!document.fullscreenElement;
    const glbActive = store.glbActive();
    const glbProgress = store.glbProgress();
    const glbReady = store.glbUrl() !== null;
    const in3D = store.viewMode() === "3d";
    const mobile = store.isMobile.value;
    // Bounding box dimensions of the loaded DXF, converted from the raw
    // coord unit (`dxfUnit`) to the display unit (`viewerUnit`) via the
    // millimetre ratio. Entities in memory are never rescaled -- this is a
    // pure display conversion, so changing viewerUnit costs nothing.
    const b = store.bounds();
    const dxfUnit = store.dxfUnit();
    const viewerUnit = store.viewerUnit();
    const displayFactor = MM_PER_UNIT[dxfUnit] / MM_PER_UNIT[viewerUnit];
    const dims = b
      ? `(${formatDim(b.maxX * displayFactor)} x ${formatDim(b.maxY * displayFactor)} ${viewerUnit})`
      : "";

    const modeToggle = html`
      <div class="mode-toggle">
        <button
          ${tooltip("2D layer view")}
          class=${!in3D ? "active" : ""}
          @click=${() => setViewMode("2d")}
        >
          2D
        </button>
        <button
          ${tooltip(
            !glbReady
              ? glbActive
                ? "Building 3D preview…"
                : "3D preview (load a DXF first)"
              : "3D solid preview"
          )}
          class=${in3D ? "active" : ""}
          ?disabled=${!glbReady}
          @click=${() => {
            if (glbReady) setViewMode("3d");
          }}
        >
          3D
        </button>
      </div>
    `;

    return html`
      <div class="left">
        <span class="brand-mark" aria-label="DXF Layer Editor" role="img">📐</span>
        <span class="title">DXF Layer Editor</span>
        ${filename
          ? html`<span class="filename" ${tooltip(filename)}>
              ${filename}${dims ? html` <span class="dims">${dims}</span>` : ""}
            </span>`
          : ""}
      </div>
      <div class="center"></div>
      <div class="actions">
        ${iconButton("Open DXF", ICONS.open, () => openFilePicker(loadDxfFromFile))}
        ${iconButton("Close DXF", ICONS.close, closeDxf, !loaded)}
        ${mobile
          ? nothing
          : iconButton(
              "Export DXF",
              ICONS.export,
              () => openModal("export", {}),
              !loaded,
              false,
              false,
              "mobile-hidden"
            )}
        ${mobile ? nothing : html`<span class="separator mobile-hidden"></span>`}
        ${iconButton(
          in3D ? "Reset 3D view" : "Fit to Window",
          ICONS.fit,
          fitCurrentView,
          !loaded
        )}
        ${
          // 2D-only chrome hides in 3D mode; and everything in this block
          // moves to the overflow menu on mobile so it's only rendered inline
          // on desktop.
          in3D || mobile
            ? nothing
            : html`
                ${iconButton(
                  gridOn ? "Hide Grid" : "Show Grid",
                  ICONS.grid,
                  toggleGrid,
                  false,
                  gridOn
                )}
                ${iconButton(
                  rulerOn ? "Hide Grid Ruler" : "Show Grid Ruler",
                  ICONS.ruler,
                  toggleGridRuler,
                  false,
                  rulerOn
                )}
              `
        }
        ${mobile
          ? nothing
          : iconButton(
              isFullscreen ? "Exit Full Screen" : "Full Screen",
              ICONS.fullscreen,
              () => this.#toggleFullscreen(),
              false,
              isFullscreen
            )}
        ${mobile
          ? nothing
          : iconButton("Units & Grid Settings", ICONS.settings, () =>
              openModal("unit-settings", {})
            )}
        ${mobile ? nothing : html`<span class="separator mobile-hidden"></span>`}
        ${
          in3D
            ? ""
            : iconButton(
                !loaded
                  ? "Layers Panel (load a DXF first)"
                  : sidebarVisible
                    ? "Hide Layers Panel"
                    : "Show Layers Panel",
                ICONS.layers,
                toggleSidebar,
                !loaded,
                loaded && sidebarVisible
              )
        }
        ${glbActive ? progressRing(glbProgress) : ""}
        ${modeToggle}
        ${mobile
          ? nothing
          : iconButton(
              isDark ? "Switch to Light Theme" : "Switch to Dark Theme",
              isDark ? ICONS.sun : ICONS.moon,
              toggleTheme
            )}
        ${mobile
          ? html`
              <button
                class="overflow-btn ${this.overflowOpen ? "active" : ""}"
                ${tooltip("More")}
                aria-label="More actions"
                aria-expanded=${this.overflowOpen ? "true" : "false"}
                @click=${(e: MouseEvent) => {
                  e.stopPropagation();
                  this.#toggleOverflow();
                }}
              >
                <svg viewBox="0 0 24 24">${ICONS.more}</svg>
              </button>
            `
          : nothing}
      </div>
      ${mobile && this.overflowOpen
        ? this.#renderOverflowMenu({
            loaded,
            in3D,
            gridOn,
            rulerOn,
            isDark,
            isFullscreen,
          })
        : nothing}
    `;
  }

  // Kebab menu opened from the mobile topbar. Each row is a 44px tap
  // target with the same action + tooltip as the desktop icon it
  // shadows, so users don't relearn anything between form factors.
  #renderOverflowMenu(ctx: {
    loaded: boolean;
    in3D: boolean;
    gridOn: boolean;
    rulerOn: boolean;
    isDark: boolean;
    isFullscreen: boolean;
  }) {
    const run = (fn: () => void) => () => {
      this.#closeOverflow();
      fn();
    };
    return html`
      <div
        class="overflow-menu"
        role="menu"
        @click=${(e: Event) => e.stopPropagation()}
      >
        <button
          class="row"
          role="menuitem"
          ?disabled=${!ctx.loaded}
          @click=${run(() => openModal("export", {}))}
        >
          <svg viewBox="0 0 24 24" fill="none"
            stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round">${ICONS.export}</svg>
          <span class="label">Export DXF</span>
        </button>
        <div class="divider"></div>
        ${ctx.in3D
          ? nothing
          : html`
              <button
                class="row ${ctx.gridOn ? "active" : ""}"
                role="menuitem"
                aria-pressed=${ctx.gridOn ? "true" : "false"}
                @click=${run(toggleGrid)}
              >
                <svg viewBox="0 0 24 24" fill="none"
                  stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round">${ICONS.grid}</svg>
                <span class="label">Grid</span>
                <span class="check">On</span>
              </button>
              <button
                class="row ${ctx.rulerOn ? "active" : ""}"
                role="menuitem"
                aria-pressed=${ctx.rulerOn ? "true" : "false"}
                @click=${run(toggleGridRuler)}
              >
                <svg viewBox="0 0 24 24" fill="none"
                  stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round">${ICONS.ruler}</svg>
                <span class="label">Ruler</span>
                <span class="check">On</span>
              </button>
            `}
        <button
          class="row"
          role="menuitem"
          @click=${run(() => openModal("unit-settings", {}))}
        >
          <svg viewBox="0 0 24 24" fill="none"
            stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round">${ICONS.settings}</svg>
          <span class="label">Units &amp; Grid…</span>
        </button>
        <button
          class="row ${ctx.isFullscreen ? "active" : ""}"
          role="menuitem"
          @click=${run(() => this.#toggleFullscreen())}
        >
          <svg viewBox="0 0 24 24" fill="none"
            stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round">${ICONS.fullscreen}</svg>
          <span class="label">${ctx.isFullscreen ? "Exit Full Screen" : "Full Screen"}</span>
        </button>
        <div class="divider"></div>
        <button
          class="row"
          role="menuitem"
          @click=${run(toggleTheme)}
        >
          <svg viewBox="0 0 24 24" fill="none"
            stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round">${ctx.isDark ? ICONS.sun : ICONS.moon}</svg>
          <span class="label">${ctx.isDark ? "Light Theme" : "Dark Theme"}</span>
        </button>
      </div>
    `;
  }
}

// Compact 20px SVG ring. Radius 8, circumference ≈ 50.27 — the fill arc's
// stroke-dashoffset shrinks from full to zero as `pct` walks 0..1.
function progressRing(pct: number) {
  const r = 8;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.max(0, Math.min(1, pct)));
  return html`
    <span class="glb-progress" ${tooltip(`Building 3D preview… ${Math.round(pct * 100)}%`)}>
      <svg viewBox="0 0 20 20">
        <circle class="track" cx="10" cy="10" r=${r} fill="none"></circle>
        <circle
          class="fill"
          cx="10"
          cy="10"
          r=${r}
          fill="none"
          stroke-dasharray=${c}
          stroke-dashoffset=${offset}
          stroke-linecap="round"
        ></circle>
      </svg>
    </span>
  `;
}

function iconButton(
  title: string,
  path: ReturnType<typeof svg>,
  onClick: () => void,
  disabled = false,
  active = false,
  featured = false,
  extraClass = ""
) {
  const classes = [active && "active", featured && "featured", extraClass]
    .filter(Boolean)
    .join(" ");
  return html`
    <button
      ${tooltip(title)}
      aria-label=${title}
      ?disabled=${disabled}
      class=${classes}
      @click=${() => {
        if (!disabled) onClick();
      }}
    >
      <svg viewBox="0 0 24 24">${path}</svg>
    </button>
  `;
}

// Trim a bounding-box dimension for display: integers stay integer, fractions
// are rounded to two decimals with trailing zeros stripped so "56.15" and "28"
// both look clean side-by-side.
function formatDim(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

declare global {
  interface HTMLElementTagNameMap {
    "editor-topbar": EditorTopbar;
  }
}
